/* eslint-disable @eslint-react/no-clone-element */
import { useIsVisible } from '@folklore/hooks';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { cloneElement, ReactNode } from 'react';

import usePlaceholderItems from '../../hooks/usePlaceholderItems';

import { useIsIdle } from '../../contexts/IdleContext';
import { useIsCapture } from '../../contexts/LayoutContext';
import ItemsLoader, { ItemsLoaderHook } from './ItemsLoader';
import List, { ListItem, ListProps } from './List';
import * as ListPresentations from './presentations';

export interface PresentationListProps<Item = ListItem> extends ListProps<Item> {
    hook?: ItemsLoaderHook<Item>;
    type?: string | null;
    title?: ReactNode;
    count?: number | null;
    query?: Record<string, unknown> | null;
    disabled?: boolean;
    initialData?: Item[] | null;
    loading?: null | 'lazy';
    presentation?: 'grid' | 'row' | 'responsive' | 'layout' | 'inline';
    paginated?: boolean;
    placeholderItem?: Record<string, unknown> | null;
    placeholderCount?: number | null;
    withoutLoader?: boolean;
    className?: string | null;
    rowClassName?: string | null;
    gridClassName?: string | null;
    onLoaded?: (items: Item[]) => void;
    [key: string]: unknown;
}

function PresentationList<Item>({
    hook,
    presentation = 'grid',
    count = null,
    query = null,
    disabled = false,
    initialData = null,
    items = null,
    loading = null,
    paginated = true,
    placeholderItem = null,
    placeholderCount = null,
    withoutLoader = false,
    className = null,
    rowClassName = null,
    gridClassName = null,
    onLoaded = null,
    ...props
}: PresentationListProps<Item>) {
    const isCapture = useIsCapture();
    const isLazyLoading = loading === 'lazy' && !isCapture;
    const { ref, visible: isVisible } = useIsVisible({
        persist: true,
        disabled: !isLazyLoading,
    });
    const shouldLoad = !isLazyLoading || isVisible;

    const placeholderItems = usePlaceholderItems(placeholderCount || count || 3, placeholderItem);
    const finalQuery = {
        count,
        ...query,
    };

    const isIdle = useIsIdle();

    const finalPresentation = presentation || 'grid';
    const finalDisabled = disabled || !shouldLoad || isIdle;

    const ListComponent = getComponentFromName(ListPresentations, finalPresentation, List);
    const list = (
        <ListComponent
            {...props}
            count={count}
            ref={ref}
            className={classNames([
                className,
                {
                    [gridClassName]: gridClassName !== null && finalPresentation === 'grid',
                    [rowClassName]: rowClassName !== null && finalPresentation === 'row',
                },
            ])}
        />
    );

    return withoutLoader ? (
        cloneElement(list, {
            items: items || placeholderItems,
        })
    ) : (
        <ItemsLoader<Item>
            hook={hook}
            query={finalQuery}
            disabled={finalDisabled}
            // keepPreviousData={keepPreviousData}
            initialData={initialData}
            placeholderData={
                paginated
                    ? {
                          data: placeholderItems,
                      }
                    : placeholderItems
            }
            onLoaded={onLoaded}
        >
            {list}
        </ItemsLoader>
    );
}

export default PresentationList;
